// the Crab-o-meter (model of Oct 26): for each day, every high tide gets a casting window, scored on surf (wave power
// in kJ, the figure FishNotify shows), the Golden Gate current, wind, light and recent rain, with the weights, ranges
// and caps in config/crab.json. Conditions only, never regulations.
import { getTimes } from '../../vendor/auto/suncalc.js';
import { dayKey } from './format.mjs';
import { data } from './snapshot.mjs';
import tierOf from './advisories.mjs';

const { crab, water } = window.RHL;
const HOUR = 3600000;

const clamp01 = (value) => Math.max(0, Math.min(1, value));
// 1 at `full`, 0 at `zero`, linear between; works whichever way round the two are
const scale = (value, full, zero) => clamp01((value - zero) / (full - zero));

const word = (score) => {
	if (score === null) return '';
	if (score >= crab.words.good) return 'good';
	if (score >= crab.words.fair) return 'fair';
	return 'poor';
};

// hourly forecast values whose hour falls inside the window
const inWindow = (series, field, start, end) => {
	if (!series?.times) return [];
	return series.times
		.map((t, i) => [new Date(t).getTime(), series[field][i]])
		.filter(([t, v]) => t >= start - HOUR / 2 && t <= end + HOUR / 2 && v !== null)
		.map(([, v]) => v);
};
const mean = (values) => (values.length ? values.reduce((a, b) => a + b, 0) / values.length : null);
// directions average as vectors, so 350° and 10° make north rather than south
const meanDirection = (degrees) => {
	if (!degrees.length) return null;
	const rad = degrees.map((d) => (d * Math.PI) / 180);
	const angle = Math.atan2(mean(rad.map(Math.sin)), mean(rad.map(Math.cos)));
	return ((angle * 180) / Math.PI + 360) % 360;
};

// the sun for a calendar day in the channel's time zone; 20:00 UTC is midday on the Pacific coast
const sunFor = (key) => {
	const [y, m, d] = key.split('-').map(Number);
	const times = getTimes(new Date(Date.UTC(y, m - 1, d, 20)), water.crabSpot.lat, water.crabSpot.lon);
	return { rise: times.sunrise.getTime(), set: times.sunset.getTime() };
};

// the Golden Gate current's speed at a moment, from the max-and-slack predictions: a sine between slack (0) and the
// neighbouring maximum
const currentAt = (events, t) => {
	if (!events?.length) return null;
	const times = events.map((e) => new Date(e.t).getTime());
	const i = times.findIndex((et) => et > t);
	if (i <= 0) return null;
	const [a, b] = [events[i - 1], events[i]];
	const [ta, tb] = [times[i - 1], times[i]];
	const f = (t - ta) / (tb - ta);
	const slackFirst = a.type === 'slack';
	const peak = slackFirst ? b.kt : a.kt;
	return peak * Math.sin((slackFirst ? f : 1 - f) * (Math.PI / 2));
};
const between = (deg, [from, to]) => (from <= to ? deg >= from && deg <= to : deg >= from || deg <= to);
const sum = (values) => values.reduce((a, b) => a + b, 0);

const scoreWindow = (high, {
	swell, wind, currents, alerts,
}) => {
	const t = new Date(high.t).getTime();
	const start = t - crab.window.hoursBefore * HOUR;
	const end = t + crab.window.hoursAfter * HOUR;
	const sun = sunFor(dayKey(t));
	const daylight = clamp01((Math.min(end, sun.set) - Math.max(start, sun.rise)) / (end - start));
	const twilight = [sun.rise, sun.set].some((edge) => start <= edge + crab.light.twilightHours * HOUR && end >= edge - crab.light.twilightHours * HOUR);

	const swellFt = Math.max(...inWindow(swell, 'ft', start, end), -Infinity);
	const swellPeriod = mean(inWindow(swell, 'periodS', start, end));
	// wave power from total waves where the forecast has them, else the swell
	const waveFt = Math.max(...inWindow(swell, 'waveFt', start, end), -Infinity);
	const wavePeriod = mean(inWindow(swell, 'wavePeriodS', start, end));
	const H = Number.isFinite(waveFt) ? waveFt : swellFt;
	const T = wavePeriod ?? swellPeriod;
	const powerKj = Number.isFinite(H) && T ? crab.surf.kjPerFt2S2 * H * H * T * T : null;

	const windKt = mean(inWindow(wind, 'kt', start, end));
	const windDir = meanDirection(inWindow(wind, 'dirDeg', start, end));
	const onshore = windDir !== null && between(windDir, crab.wind.onshoreFromDeg);
	const rainMm = wind?.rainMm ? sum(inWindow({ times: wind.times, rainMm: wind.rainMm }, 'rainMm', start - 48 * HOUR, start)) : null;
	const current = currents?.events ? mean([start, (start + end) / 2, end].map((x) => currentAt(currents.events, x)).filter((v) => v !== null)) : null;

	const parts = {
		surf: powerKj !== null ? scale(powerKj, crab.surf.fullKj, crab.surf.zeroKj) : null,
		current: current !== null ? scale(current, crab.current.fullKt, crab.current.zeroKt) : null,
		wind: windKt !== null ? scale(windKt + (onshore ? crab.wind.onshoreAddKt : 0), crab.wind.fullKt, crab.wind.zeroKt) : null,
		light: clamp01(daylight + (twilight ? crab.light.twilightBonus : 0)),
		rain: rainMm !== null ? scale(rainMm, crab.rain.fullMm, crab.rain.zeroMm) : null,
	};
	// a part without a forecast drops out and the others carry its weight
	const known = Object.entries(parts).filter(([, value]) => value !== null);
	const weight = known.reduce((total, [name]) => total + crab.weights[name], 0);
	const total = known.reduce((acc, [name, value]) => acc + crab.weights[name] * value, 0) / weight;
	let score = Math.round(total * 10);

	// caps: a surf or beach hazard in effect over the window, very big surf, a window entirely in the dark
	const hazard = (alerts ?? []).some((a) => tierOf(a.event) === 'crab' && (!a.ends || new Date(a.ends).getTime() > start) && (!a.onset || new Date(a.onset).getTime() < end));
	if (hazard) score = Math.min(score, crab.caps.alert);
	if (powerKj !== null && powerKj > crab.caps.bigSurfKj) score = Math.min(score, crab.caps.bigSurf);
	if (daylight === 0) score = Math.min(score, crab.caps.dark);

	return {
		high,
		start,
		end,
		sun,
		score,
		parts,
		powerKj: powerKj !== null ? Math.round(powerKj) : null,
		hazard,
		swell: Number.isFinite(swellFt) ? { ft: swellFt, periodS: swellPeriod, word: word(parts.surf) } : null,
		wind: windKt !== null ? {
			kt: windKt, dirDeg: windDir, onshore, word: word(parts.wind),
		} : null,
		currentKt: current,
		complete: known.length === Object.keys(parts).length,
	};
};

// the best window for each of the next crab.days days that have one; today's only counts windows that haven't
// ended, so late in the day the list starts tomorrow and runs a day further
const crabDays = ({
	tides, swell, wind, currents = data('currents'), alerts = data('alerts'),
}, now = new Date()) => {
	if (!tides?.events) return [];
	const days = [];
	for (let i = 0; i <= crab.days && days.length < crab.days; i += 1) {
		const key = dayKey(now.getTime() + i * 24 * HOUR);
		const windows = tides.events
			.filter((e) => e.type === 'H' && dayKey(e.t) === key)
			.map((high) => scoreWindow(high, {
				swell, wind, currents, alerts,
			}))
			.filter((w) => w.end > now.getTime());
		if (windows.length) {
			windows.sort((a, b) => b.score - a.score || a.start - b.start);
			days.push({ key, ...windows[0] });
		}
	}
	return days;
};

export default crabDays;
